/**
 * /permission-ceremony — Surface Layer (/01)
 *
 * F0.2 Carbon shell (placeholder_only). Lets an actor review which tool
 * routes are available in the current tenant / session context and which
 * require a gate. Mirrors AUTH_ACTOR_TOOL_MATRIX (default-deny, fail-closed:
 * a tool that needs tenant context cannot be allowed without it).
 *
 * Data comes ONLY from usePermissionCeremony() (local mock, usingFallback=true).
 * NO ToolGateway / grant call is made and NO tool is ever executed here — the
 * real grant flow (ToolGateway-validated, session-strength + PBAC scoped) is a
 * later gated step (B2 / B3). The action button is disabled. Visual refinement
 * is F0.3.
 *
 * Carbon authority: installed @carbon/react v1.108.0 + verified existing /01
 * patterns (mirrors src/routes/ops.tsx empty-state view) — not training data.
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, Column, Grid, InlineNotification, Tag, Tile } from '@carbon/react';
import { usePermissionCeremony } from '@lib/persistence/usePermissionCeremony';
import '../styles/views.scss';

export const Route = createFileRoute('/permission-ceremony')({
  component: PermissionCeremonyView,
});

function PermissionCeremonyView() {
  const { state, usingFallback } = usePermissionCeremony();
  const contextOk = state.tenantContextPresent || !state.tenantContextRequired;

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Review tool permissions</h1>
        <p className="view__subtitle">
          Actor: {state.actorType} · session strength: {state.sessionStrength}. Default-deny — a
          tool that needs tenant context cannot be allowed without it.
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <InlineNotification
              kind={usingFallback ? 'warning' : 'info'}
              lowContrast
              hideCloseButton
              title={usingFallback ? 'Sample — no live session / registry' : 'Live tool permissions (read-only)'}
              subtitle={
                usingFallback
                  ? 'No signed-in session or no live ToolGateway registry, so these are a labelled sample (default-deny). Nothing is granted or executed.'
                  : 'Read live from the workspace ToolGateway system-of-record for your actor + tenant context. This view never grants or executes a tool; the actual step-up (passkey) is an operator action.'
              }
            />
          </Column>

          {!contextOk ? (
            <Column sm={4} md={8} lg={16}>
              <InlineNotification
                kind="warning"
                lowContrast
                hideCloseButton
                title="Tenant context required"
                subtitle="Tools that need tenant context stay denied until a workspace is selected."
              />
            </Column>
          ) : null}

          {state.tools.map((tool) => (
            <Column key={tool.toolRoute} sm={4} md={4} lg={4}>
              <Tile className="view-tile">
                <div className="view-tile__row">
                  <h2 className="view-tile__value">{tool.label}</h2>
                  <Tag type={tool.allowed ? 'teal' : 'red'}>
                    {tool.allowed ? 'Allowed' : 'Denied'}
                  </Tag>
                </div>
                <p className="view-tile__label">Route: {tool.toolRoute}</p>
                <div className="view-actions">
                  {tool.requiresGate ? <Tag type="blue">Gate required</Tag> : null}
                  {tool.requiresSecret ? <Tag type="magenta">Secret required</Tag> : null}
                  {!tool.requiresGate && !tool.requiresSecret ? (
                    <Tag type="cool-gray">No gate</Tag>
                  ) : null}
                </div>
                <Button kind="tertiary" disabled>
                  Request access
                </Button>
              </Tile>
            </Column>
          ))}
        </Grid>
      </div>
    </div>
  );
}
